import { useCallback, useMemo, useState } from 'react';
import type { Node } from '@xyflow/react';

import { NodeType, SidebarView, WorkflowSidebarControlProps } from './use-sidebar-control.types';
import { NodeEditModeEnum } from '../../workflow-sidebar/edit-node-form/edit-node-form.types';
import type { WorkflowGraphControl } from '../use-workflow-graph';

export const useSidebarControl = (
  graph: WorkflowGraphControl
): WorkflowSidebarControlProps & {
  openEdit: (node: Node) => void;
  openNodeLibrary: () => void;
} => {
  const [editNodeId, setEditNodeId] = useState<string | null>(null);
  const [draftNode, setDraftNode] = useState<Node | null>(null);

  const editedNode = useMemo(
    () => graph.nodes.find((n) => n.id === editNodeId) ?? null,
    [editNodeId, graph.nodes]
  );

  const openEdit = useCallback(
    (node: Node) => {
      setEditNodeId(node.id);
      setDraftNode(null);
      graph.openSidebar();
    },
    [graph]
  );

  const close = useCallback(() => {
    graph.closeSidebar();
    setTimeout(() => {
      setEditNodeId(null);
      setDraftNode(null);
    }, 300);
  }, [graph]);

  const backToLibrary = useCallback(() => {
    setEditNodeId(null);
    setDraftNode(null);
  }, []);

  const openNodeLibrary = useCallback(() => {
    setEditNodeId(null);
    setDraftNode(null);
    graph.openSidebar();
  }, [graph]);

  const addDraftNode = useCallback(
    (type: NodeType.Wait | NodeType.Action) => {
      const draftNode: Node = {
        id: `draft-${type}-${crypto.randomUUID()}`,
        type,
        position: { x: 0, y: 0 },
        data: {},
      };
      setDraftNode(draftNode);
      setEditNodeId(null);
      graph.openSidebar();
    },
    [graph]
  );

  const saveDraftNode = useCallback(
    (data: Record<string, unknown>) => {
      if (!draftNode) {
        return;
      }

      if (draftNode.type === NodeType.Enroll) {
        graph.addEnroll(data);
      } else if (draftNode.type === NodeType.Wait) {
        graph.addNodeAtPlaceholder(NodeType.Wait, data);
      } else if (draftNode.type === NodeType.Action) {
        graph.addNodeAtPlaceholder(NodeType.Action, data);
      }

      setDraftNode(null);
    },
    [draftNode, graph]
  );

  const saveExistingNode = useCallback(
    (data: Record<string, unknown>) => {
      if (!editedNode) {
        return;
      }
      graph.updateNodeData(editedNode.id, data);
      setEditNodeId(null);
    },
    [editedNode, graph]
  );

  const view: SidebarView = useMemo(() => {
    if (editedNode) {
      return { kind: NodeEditModeEnum.EditExisting, node: editedNode };
    }
    if (draftNode) {
      return { kind: NodeEditModeEnum.EditDraft, node: draftNode };
    }
    return { kind: NodeEditModeEnum.Library };
  }, [editedNode, draftNode]);

  return {
    open: graph.isNodeSidebarOpen,
    close,
    view,
    addDraftNode,
    backToLibrary,
    saveDraftNode,
    saveExistingNode,
    openEdit,
    openNodeLibrary,
  };
};
